import { Link, useRoute } from '@buzola/router'
import type { ReactNode } from 'react'
import { useWorkflowAction } from '../../lib/rpc'
import { type AdminSection, adminSectionForPath, useAdminSectionAccess } from '../lib/section-access'
import { useAdminToast } from './toast'

type SnRoute =
	| 'dashboard'
	| 'search'
	| 'course-terms'
	| 'course-interests'
	| 'contact-forms'
	| 'contact-form-submissions'
	| 'courses'
	| 'orders'
	| 'organizations'
	| 'course-tags'
	| 'discount-codes'
	| 'emails'
	| 'email-texts'
	| 'members'
	| 'api-keys'

type GdRoute =
	| 'gd'
	| 'gd/search'
	| 'gd/calls'
	| 'gd/schvaleni'
	| 'gd/issuers'
	| 'gd/subscribers'
	| 'gd/users'
	| 'gd/orders'
	| 'gd/discount-codes'
	| 'gd/demo-users'
	| 'gd/plans'
	| 'gd/taxonomies'
	| 'gd/testimonials'
	| 'gd/emails'
	| 'gd/email-texts'
	| 'gd/chrome'
	| 'gd/pages'
	| 'gd/page-edit'
	| 'gd/settings'
	| 'gd/checkout-probes'

type CmsRoute =
	| 'cms/pages'
	| 'cms/articles'
	| 'cms/people'
	| 'cms/tags'
	| 'cms/podcasts'
	| 'cms/redirects'
	| 'cms/navigation'
	| 'cms/footer'
	| 'cms/support-ctas'
	| 'cms/banners'
	| 'cms/site-settings'

type NavItem = {
	route: SnRoute | GdRoute | CmsRoute
	label: string
	icon: ReactNode
}

/** A group without a `label` renders as a bare run of links — no heading. */
type NavGroup = {
	label?: string
	items: NavItem[]
}

type NavSection = {
	key: AdminSection
	short: string
	label: string
	subtitle: string
	home: 'dashboard' | 'gd' | 'cms/articles'
	groups: NavGroup[]
	/** Podklad vybrané záložky v přepínači sekcí. Výchozí je bílá. */
	activeTab?: string
}

/**
 * Same layering as the CMS tab: what the team looks at daily, the course
 * catalogue, the people who sign up, the mail that goes out, and what is
 * set once. Fifteen identical rows were the hardest tab to scan.
 */
const snGroups: NavGroup[] = [
	{
		items: [
			{ route: 'dashboard', label: 'Dashboard', icon: <HomeIcon /> },
			{ route: 'search', label: 'Hledat', icon: <SearchIcon /> },
		],
	},
	{
		label: 'Kurzy',
		items: [
			{ route: 'courses', label: 'Kurzy', icon: <BookIcon /> },
			{ route: 'course-terms', label: 'Termíny', icon: <CalendarIcon /> },
			{ route: 'course-tags', label: 'Štítky', icon: <TagIcon /> },
		],
	},
	{
		label: 'Účastníci',
		items: [
			{ route: 'orders', label: 'Přihlášky', icon: <TicketIcon /> },
			{ route: 'course-interests', label: 'Zájemci', icon: <HeartIcon /> },
			{ route: 'organizations', label: 'Organizace', icon: <BuildingIcon /> },
			{ route: 'discount-codes', label: 'Slevové kódy', icon: <PercentIcon /> },
		],
	},
	{
		label: 'Komunikace',
		items: [
			{ route: 'emails', label: 'Emaily', icon: <MailIcon /> },
			{ route: 'email-texts', label: 'Texty e-mailů', icon: <FileTextIcon /> },
			{ route: 'contact-forms', label: 'Formuláře', icon: <FormIcon /> },
			{ route: 'contact-form-submissions', label: 'Formuláře – odpovědi', icon: <MailIcon /> },
		],
	},
	{
		label: 'Nastavení',
		items: [
			{ route: 'members', label: 'Členové', icon: <UsersIcon /> },
			{ route: 'api-keys', label: 'API klíče', icon: <KeyIcon /> },
		],
	},
]

const gdGroups: NavGroup[] = [
	{
		items: [
			{ route: 'gd', label: 'Přehled', icon: <HomeIcon /> },
			{ route: 'gd/search', label: 'Hledat', icon: <SearchIcon /> },
		],
	},
	{
		label: 'Odběratelé',
		items: [
			{ route: 'gd/orders', label: 'Objednávky', icon: <TicketIcon /> },
			{ route: 'gd/subscribers', label: 'Odběratelé', icon: <UsersIcon /> },
			{ route: 'gd/users', label: 'Uživatelé', icon: <UserIcon /> },
			{ route: 'gd/demo-users', label: 'Demo uživatelé', icon: <HeartIcon /> },
		],
	},
	{
		label: 'Výzvy',
		items: [
			{ route: 'gd/calls', label: 'Výzvy', icon: <MegaphoneIcon /> },
			{ route: 'gd/issuers', label: 'Vyhlašovatelé', icon: <BuildingIcon /> },
			{ route: 'gd/schvaleni', label: 'Ke schválení', icon: <InboxIcon /> },
		],
	},
	{
		label: 'Web',
		items: [
			{ route: 'gd/pages', label: 'Stránky webu', icon: <LayoutIcon /> },
			{ route: 'gd/chrome', label: 'Hlavička a patička', icon: <PanelTopIcon /> },
			{ route: 'gd/testimonials', label: 'Reference', icon: <QuoteIcon /> },
		],
	},
	{
		label: 'Nastavení',
		items: [
			{ route: 'gd/emails', label: 'E-maily', icon: <MailIcon /> },
			{ route: 'gd/email-texts', label: 'Texty e-mailů', icon: <FileTextIcon /> },
			{ route: 'gd/discount-codes', label: 'Slevové kódy', icon: <PercentIcon /> },
			{ route: 'gd/checkout-probes', label: 'Dotazy na IČO', icon: <ShieldIcon /> },
			{ route: 'gd/plans', label: 'Ceník', icon: <TagIcon /> },
			{ route: 'gd/taxonomies', label: 'Číselníky', icon: <ListIcon /> },
			{ route: 'gd/settings', label: 'Nastavení', icon: <SettingsIcon /> },
		],
	},
]

/**
 * Split into what an editor writes, what wraps every page, and what is set once —
 * ten identical rows read as one undifferentiated list, which is exactly how it felt.
 * The headings always show; nothing here collapses.
 */
const cmsGroups: NavGroup[] = [
	{
		label: 'Obsah',
		items: [
			{ route: 'cms/articles', label: 'Články', icon: <FileTextIcon /> },
			{ route: 'cms/pages', label: 'Stránky', icon: <LayoutIcon /> },
			{ route: 'cms/podcasts', label: 'Podcasty', icon: <MicIcon /> },
			{ route: 'cms/people', label: 'Lidé', icon: <UsersIcon /> },
			{ route: 'cms/tags', label: 'Štítky', icon: <TagIcon /> },
		],
	},
	{
		label: 'Layout webu',
		items: [
			{ route: 'cms/navigation', label: 'Navigace', icon: <ArrowUpIcon /> },
			{ route: 'cms/footer', label: 'Patička', icon: <ArrowDownIcon /> },
			{ route: 'cms/support-ctas', label: 'CTA boxy', icon: <MegaphoneIcon /> },
			{ route: 'cms/banners', label: 'Bannery', icon: <ImageIcon /> },
		],
	},
	{
		label: 'Nastavení',
		items: [
			{ route: 'cms/site-settings', label: 'Info webu', icon: <SettingsIcon /> },
			{ route: 'cms/redirects', label: 'Přesměrování', icon: <ArrowRightIcon /> },
		],
	},
]

const sections: NavSection[] = [
	{ key: 'cms', short: 'CMS', label: 'Svět neziskovek', subtitle: 'obsah webu', home: 'cms/articles', groups: cmsGroups },
	// Kurzy si nesou lososový podklad vybrané záložky — bílá byla mezi navy
	// pruhem a bílým obsahem k nerozeznání od pozadí stránky.
	{ key: 'sn', short: 'Kurzy', label: 'Svět neziskovek', subtitle: 'kurzy', home: 'dashboard', groups: snGroups, activeTab: 'bg-orange' },
	// Táž třída jako u kurzů, ale uvnitř `[data-section='gd']` je
	// `--color-orange` přemapovaná na mátovou (viz index.css), takže záložka
	// zezelená sama a barva zůstává na jednom místě.
	{ key: 'gd', short: 'GD', label: 'Grantový diář', subtitle: 'administrace', home: 'gd', groups: gdGroups, activeTab: 'bg-orange' },
]

export function NavSidebar() {
	const { pathname } = useRoute()
	const canAccess = useAdminSectionAccess()
	const visibleSections = sections.filter(section => canAccess(section.key))

	// The URL is the source of truth for the active section, so a deep link, a
	// reload and the back button all agree without any stored preference. A deep
	// link into a section the role cannot open falls back to the first one it can,
	// so the sidebar stays usable next to the "nemáš oprávnění" panel.
	const activeKey = adminSectionForPath(pathname)
	const active = visibleSections.find(section => section.key === activeKey) ?? visibleSections[0]

	if (!active) return null

	return (
		<aside className="hidden md:flex md:w-60 lg:w-64 shrink-0 flex-col bg-blue-primary text-white sticky top-0 h-screen">
			<div className="px-6 py-6 border-b border-white/10">
				<Link className="block" to={active.home}>
					<div className="font-vag-normal text-orange text-2xl leading-tight">
						{active.label}
					</div>
					<div className="font-vag-light text-white/70 text-sm tracking-wide">
						{active.subtitle}
					</div>
				</Link>
			</div>

			<div className={visibleSections.length > 1 ? 'px-3 pt-3' : 'hidden'}>
				<div className="flex rounded-md bg-white/10 p-0.5" role="tablist">
					{visibleSections.map(section => (
						<Link
							aria-selected={section.key === active.key}
							className={[
								'flex-1 rounded-sm px-2 py-1.5 text-center text-xs font-medium transition-colors',
								section.key === active.key
									? `text-blue-primary ${section.activeTab ?? 'bg-white'}`
									: 'text-white/70 hover:text-white',
							].join(' ')}
							key={section.key}
							role="tab"
							to={section.home}
						>
							{section.short}
						</Link>
					))}
				</div>
			</div>

			<nav className="flex-1 overflow-y-auto py-4">
				{active.groups.map((group, index) => {
					const headingId = group.label ? `nav-group-${active.key}-${index}` : undefined
					return (
						<div className={index > 0 ? 'mt-5' : undefined} key={group.label ?? '_'}>
							{group.label && (
								<h2
									className="px-4 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-white/45"
									id={headingId}
								>
									{group.label}
								</h2>
							)}
							<ul aria-labelledby={headingId} className="space-y-0.5 px-3">
								{group.items.map(item => (
									<li key={item.route}>
										<Link
											activeExact={item.route === 'gd'}
											className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-white/80 transition-colors hover:bg-white/10 hover:text-white border-l-4 border-transparent data-active:border-orange data-active:bg-white/10 data-active:text-white data-active:font-medium"
											to={item.route}
										>
											<span className="text-base">{item.icon}</span>
											{item.label}
										</Link>
									</li>
								))}
								{active.key === 'cms' && group.label === 'Nastavení' ? <SearchReindexButton /> : null}
							</ul>
						</div>
					)
				})}
			</nav>
			<footer className="border-t border-white/10 px-4 py-3 text-xs text-white/60">
				<div>Contember admin</div>
				<a className="text-white/80 hover:text-orange transition-colors" href="/">
					Přejít na web →
				</a>
			</footer>
		</aside>
	)
}

/**
 * "Přeindexovat hledání": the worker dispatches `search-index.yaml`, which rebuilds the website
 * search from Contember into R2 in a couple of minutes. It also runs after every deploy and nightly,
 * so this is only for an editor who wants a new article searchable right away.
 */
function SearchReindexButton() {
	const toast = useAdminToast()
	const { run, state } = useWorkflowAction('search.reindex', {
		onSuccess: () => {
			toast.success('Přeindexování běží, nový obsah bude ve vyhledávání na webu do pár minut.')
		},
		onError: error => {
			toast.error(`Přeindexování se nepodařilo spustit: ${error.message}`)
		},
	})

	return (
		<li>
			<button
				className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-white/80 transition-colors hover:bg-white/10 hover:text-white border-l-4 border-transparent disabled:opacity-50"
				disabled={state.isPending}
				onClick={() => void run({})}
				type="button"
			>
				<span className="text-base">
					<RefreshIcon />
				</span>
				{state.isPending ? 'Spouštím přeindexování…' : 'Přeindexovat hledání'}
			</button>
		</li>
	)
}

/* Inline lucide-style icons (kept here so we don't add a runtime dep). */
function RefreshIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
			<path d="M21 3v5h-5" />
			<path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
			<path d="M8 16H3v5" />
		</svg>
	)
}
function MailIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<rect height="16" rx="2" width="20" x="2" y="4" />
			<path d="m22 7-10 5L2 7" />
		</svg>
	)
}
function FileTextIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
			<polyline points="14 2 14 8 20 8" />
			<line x1="16" x2="8" y1="13" y2="13" />
			<line x1="16" x2="8" y1="17" y2="17" />
		</svg>
	)
}
function PanelTopIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<rect height="18" rx="2" width="18" x="3" y="3" />
			<path d="M3 9h18" />
			<path d="M3 15h18" />
		</svg>
	)
}
function CalendarIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<rect height="18" rx="2" width="18" x="3" y="4" />
			<line x1="16" x2="16" y1="2" y2="6" />
			<line x1="8" x2="8" y1="2" y2="6" />
			<line x1="3" x2="21" y1="10" y2="10" />
		</svg>
	)
}
function HomeIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
			<polyline points="9 22 9 12 15 12 15 22" />
		</svg>
	)
}
function SearchIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<circle cx="11" cy="11" r="8" />
			<path d="m21 21-4.3-4.3" />
		</svg>
	)
}
function BookIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
			<path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
		</svg>
	)
}
function HeartIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
		</svg>
	)
}
function TicketIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z" />
			<path d="M13 5v2" />
			<path d="M13 17v2" />
			<path d="M13 11v2" />
		</svg>
	)
}
function FormIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
			<polyline points="14 2 14 8 20 8" />
			<line x1="8" x2="16" y1="13" y2="13" />
			<line x1="8" x2="16" y1="17" y2="17" />
			<line x1="8" x2="10" y1="9" y2="9" />
		</svg>
	)
}
function BuildingIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<rect height="20" rx="2" ry="2" width="16" x="4" y="2" />
			<path d="M9 22v-4h6v4" />
			<path d="M8 6h.01" />
			<path d="M16 6h.01" />
			<path d="M12 6h.01" />
			<path d="M12 10h.01" />
			<path d="M8 10h.01" />
			<path d="M16 10h.01" />
			<path d="M12 14h.01" />
			<path d="M16 14h.01" />
			<path d="M8 14h.01" />
		</svg>
	)
}
function UsersIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
			<circle cx="9" cy="7" r="4" />
			<path d="M22 21v-2a4 4 0 0 0-3-3.87" />
			<path d="M16 3.13a4 4 0 0 1 0 7.75" />
		</svg>
	)
}
function KeyIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<circle cx="7.5" cy="15.5" r="5.5" />
			<path d="m21 2-9.6 9.6" />
			<path d="m15 8 3 3" />
			<path d="m18 5 3 3" />
		</svg>
	)
}
function UserIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
			<circle cx="12" cy="7" r="4" />
		</svg>
	)
}
function TagIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
			<line x1="7" x2="7.01" y1="7" y2="7" />
		</svg>
	)
}
function PercentIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<line x1="19" x2="5" y1="5" y2="19" />
			<circle cx="6.5" cy="6.5" r="2.5" />
			<circle cx="17.5" cy="17.5" r="2.5" />
		</svg>
	)
}
function ShieldIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<path d="M12 3l7 3v6c0 4-3 7.5-7 9-4-1.5-7-5-7-9V6z" />
		</svg>
	)
}
function MegaphoneIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<path d="m3 11 18-5v12L3 14v-3z" />
			<path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
		</svg>
	)
}
function ImageIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<rect height="18" rx="2" width="18" x="3" y="3" />
			<circle cx="9" cy="9" r="2" />
			<path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
		</svg>
	)
}
function InboxIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
			<path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
		</svg>
	)
}
function ListIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<line x1="8" x2="21" y1="6" y2="6" />
			<line x1="8" x2="21" y1="12" y2="12" />
			<line x1="8" x2="21" y1="18" y2="18" />
			<line x1="3" x2="3.01" y1="6" y2="6" />
			<line x1="3" x2="3.01" y1="12" y2="12" />
			<line x1="3" x2="3.01" y1="18" y2="18" />
		</svg>
	)
}
function QuoteIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<path d="M10 11H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v8a4 4 0 0 1-4 4" />
			<path d="M20 11h-4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v8a4 4 0 0 1-4 4" />
		</svg>
	)
}
function MicIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<rect height="13" rx="3" width="7" x="8.5" y="2" />
			<path d="M5 11a7 7 0 0 0 14 0" />
			<line x1="12" x2="12" y1="18" y2="22" />
		</svg>
	)
}
function LayoutIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<rect height="18" rx="2" width="18" x="3" y="3" />
			<line x1="3" x2="21" y1="9" y2="9" />
			<line x1="9" x2="9" y1="21" y2="9" />
		</svg>
	)
}
function ArrowUpIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<line x1="12" x2="12" y1="20" y2="4" />
			<polyline points="6 10 12 4 18 10" />
		</svg>
	)
}
function ArrowDownIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<line x1="12" x2="12" y1="4" y2="20" />
			<polyline points="6 14 12 20 18 14" />
		</svg>
	)
}
function ArrowRightIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<line x1="4" x2="20" y1="12" y2="12" />
			<polyline points="14 6 20 12 14 18" />
		</svg>
	)
}
function SettingsIcon() {
	return (
		<svg className="h-4 w-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24">
			<circle cx="12" cy="12" r="3" />
			<path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
		</svg>
	)
}
